import { useContext, useEffect, useMemo, useState } from 'react'
import { MenuTitles } from '../../constants/menu'
import { ContentLayout, PageLayout } from 'layout'
import { useParams } from 'react-router-dom'
import { isEqualWith } from 'lodash-es'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import {
  AgxTextInput,
  AgxDatePicker,
  AgxColumn,
  AgxRow,
  AgxLabel,
  AgxButton,
  AgxToast,
  AgxSelect,
  AgxTextArea,
  AgxMultiSelect,
  Option,
  StateAgreementFormTypeMap,
  AgxPhoneNumberInput,
} from '@urbanx/agx-ui-components'
import { AgxToastState } from 'types/commonTypes'

import { FormPrompt } from 'components/FormPrompt'
import { isDirtyCompare } from 'utils/compares'
import './agencyDetails.scss'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AgencyDetailsModel,
  Agreements,
  LicenceDetails,
  LicenceDetailsState,
  useGetAgencyDetails,
  useInvalidateUseGetAgencyDetails,
  useInvalidateUseGetAllAgencies,
  useUpdateAgency,
} from '@urbanx/agx.agencies.client'
import { useSelectedAgency } from 'hooks'

const agencyStateOptions: Option[] = [
  { value: LicenceDetailsState.ACT, label: 'ACT' },
  { value: LicenceDetailsState.NSW, label: 'NSW' },
  { value: LicenceDetailsState.QLD, label: 'QLD' },
  { value: LicenceDetailsState.NT, label: 'NT' },
  { value: LicenceDetailsState.SA, label: 'SA' },
  { value: LicenceDetailsState.TAS, label: 'TAS' },
  { value: LicenceDetailsState.VIC, label: 'VIC' },
  { value: LicenceDetailsState.WA, label: 'WA' },
]

const allStateOptions: Option[] = Object.values(LicenceDetailsState).map(
  (state) => {
    return {
      value: state.toString(),
      label: state.toString(),
    }
  }
)

const qldAgreementOptions: Option[] = [
  {
    value: Agreements.QLDResidentialForm6.toString(),
    label: Agreements.QLDResidentialForm6,
  },
]

const nswAgreementOptions: Option[] = [
  {
    value: Agreements.NSWResidentialAgreement.toString(),
    label: StateAgreementFormTypeMap.NSWResidentialAgreement,
  },
  {
    value: Agreements.NSWRuralAgreement.toString(),
    label: StateAgreementFormTypeMap.NSWRuralAgreement,
  },
]

const getAgreementOptions = (companyState: string | number) => {
  switch (companyState) {
    case LicenceDetailsState.QLD.toString():
    case LicenceDetailsState.QLD: {
      return qldAgreementOptions
    }
    case LicenceDetailsState.NSW.toString():
    case LicenceDetailsState.NSW: {
      return nswAgreementOptions
    }
    default:
      return []
  }
}

interface CompanyLicenseDetails extends LicenceDetails {
  uuid: string
}

const AgencyDetailsPage = () => {
  const { userAccount } = useContext(AuthContext)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [formLoaded, setFormLoaded] = useState(false)
  const { agencyId } = useParams()
  const [companyLicences, setCompanyLicences] = useState<
    CompanyLicenseDetails[]
  >([])
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })
  const { agency: selectedAgency } = useSelectedAgency()

  const invalidateGetAllAgencies = useInvalidateUseGetAllAgencies()
  const invalidateGetAgencyDetails = useInvalidateUseGetAgencyDetails()

  const [agencyInformation, setAgencyInformation] =
    useState<AgencyDetailsModel>({})

  const { data: agencyDetails, isPending: agencyDetailsPending } =
    useGetAgencyDetails(agencyId!, !!agencyId)

  const { updateAgency } = useUpdateAgency({
    onSuccess: async () => {
      setIsFormDirty(false)
      await invalidateGetAllAgencies()
      await invalidateGetAgencyDetails(agencyId!)
      updateToastState({
        color: 'success',
        message: `${MenuTitles.AGENCY_DETAILS} updated successfully`,
        open: true,
      })
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: `Error updating ${MenuTitles.AGENCY_DETAILS}`,
        open: true,
      })
    },
  })

  const getNextAvailableState = () => {
    const allStates = Object.values(LicenceDetailsState)

    const nextState = allStates.find(
      (i) => !companyLicences.map((cl) => cl.state).includes(i)
    )

    return nextState
  }

  const addLicence = () => {
    const nextState = getNextAvailableState()
    const newLicence: CompanyLicenseDetails = {
      licenceNumber: '',
      uuid: uuidv4(),
      state: nextState,
      expiryDate: new Date(moment().format('YYYY-MM-DDTHH:mm:ss')),
    }
    setCompanyLicences([...companyLicences, newLicence])
  }

  const removeLicence = (uuid: string) => {
    const updatedLicences = companyLicences.filter((l) => l.uuid !== uuid)
    setCompanyLicences(updatedLicences)
  }

  const updateCompanyLicences = (licence: CompanyLicenseDetails) => {
    if (companyLicences) {
      const updatedLicences = [
        ...companyLicences.map((l) => (l.uuid === licence.uuid ? licence : l)),
      ]
      setCompanyLicences(updatedLicences)

      setAgencyInformation((previousState) => ({
        ...previousState,
        companyDetails: {
          ...previousState.companyDetails,
          licences: updatedLicences,
        },
      }))
    }
  }

  useEffect(() => {
    if (!agencyDetailsPending && agencyDetails) {
      setAgencyInformation(agencyDetails)
      if (agencyDetails.companyDetails?.licences) {
        setCompanyLicences(
          agencyDetails.companyDetails.licences.map((licence) => ({
            ...licence,
            uuid: uuidv4(),
          }))
        )
      }

      setIsFormDirty(false)

      if (agencyDetails.id != null) setFormLoaded(true)
    }
  }, [agencyDetails])

  useEffect(() => {
    setIsFormDirty(
      !isEqualWith(agencyDetails, agencyInformation, isDirtyCompare)
    )
  }, [agencyInformation, agencyDetails])

  const addressStateDefaultValue = useMemo(() => {
    const selectedOption = allStateOptions.find(
      (option) => option.value === agencyInformation?.address?.state
    )
    return selectedOption ?? allStateOptions[0]
  }, [agencyInformation?.address?.state])

  if (agencyInformation == null) return null

  const {
    tradingName,
    companyDetails: { licenseeName, abn, acn } = {},
    listingsEmail,
    listingsPhone,
    address: { streetAddress, suburb, postalCode } = {},
    instructionsConditions,
  } = agencyInformation

  const sanitizeAgencyDetails = (agencyInformation: AgencyDetailsModel) => {
    const updatedLicences = companyLicences.map((licence) => {
      const { ...rest } = licence
      return { ...rest }
    })

    return {
      ...agencyInformation,
      companyDetails: {
        ...agencyInformation.companyDetails,
        licences: updatedLicences,
      },
    }
  }

  const saveChanges = () => {
    updateAgency({
      ...sanitizeAgencyDetails(agencyInformation),
    })
  }

  if (agencyDetails === undefined || agencyDetailsPending || !formLoaded)
    return null

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.AGENCY_DETAILS}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.AGENCY_DETAILS}
      >
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        <AgxToast selector="#agxToast" toastState={toastState} />
        <AgxColumn veryLargeGap>
          <AgxRow spaceBetween centered extraClasses="borderBottomContainer">
            <AgxLabel large>{MenuTitles.AGENCY_DETAILS}</AgxLabel>
            <AgxButton
              id="agency-save-changes"
              text="Save Changes"
              medium
              primary
              onClick={saveChanges}
            />
          </AgxRow>

          <AgxColumn veryLargeGap extraClasses="container50Percent">
            <AgxRow extraClasses="borderBottomContainer">
              <AgxLabel large>Company details</AgxLabel>
            </AgxRow>
            <AgxRow>
              <AgxTextInput
                id="tradingName"
                label="Trading Name"
                stretch
                noOptionalLabel
                defaultValue={tradingName || ''}
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    tradingName: value,
                  }))
                }
              />
            </AgxRow>
            <AgxRow veryLargeGap>
              <AgxTextInput
                id="abn"
                label="ABN"
                abn={true}
                stretch
                noOptionalLabel
                defaultValue={abn || ''}
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    companyDetails: {
                      ...previousState.companyDetails,
                      abn: value,
                    },
                  }))
                }
              />

              <AgxTextInput
                id="acn"
                label="ACN"
                acn={true}
                stretch
                noOptionalLabel
                defaultValue={acn || ''}
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    companyDetails: {
                      ...previousState.companyDetails,
                      acn: value,
                    },
                  }))
                }
              />
            </AgxRow>
            <AgxRow>
              <AgxTextInput
                id="licenseeName"
                label="Licensee name"
                stretch
                noOptionalLabel
                defaultValue={licenseeName || ''}
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    companyDetails: {
                      ...previousState.companyDetails,
                      licenseeName: value,
                    },
                  }))
                }
              />
            </AgxRow>
            {companyLicences?.map((licence, index) => {
              const agreementOptions = getAgreementOptions(`${licence.state}`)

              return (
                <>
                  <AgxRow
                    key="companyLicence"
                    extraClasses="borderBottomContainer"
                    spaceBetween
                  >
                    <AgxLabel medium>{`Licence ${index + 1}`}</AgxLabel>
                    {companyLicences.length > 1 && (
                      <AgxButton
                        text={'Remove'}
                        small
                        hollow
                        onClick={() => removeLicence(licence.uuid ?? '')}
                      />
                    )}
                  </AgxRow>
                  <AgxRow veryLargeGap>
                    <AgxTextInput
                      id="licenceNumber"
                      label="Licence Number"
                      stretch
                      noOptionalLabel
                      defaultValue={licence.licenceNumber || ''}
                      onInputValueChange={({ value }: { value: string }) =>
                        updateCompanyLicences({
                          licenceNumber: value,
                          expiryDate: licence.expiryDate,
                          state: licence.state,
                          agreements: licence.agreements,
                          uuid: licence.uuid,
                        })
                      }
                    />

                    <AgxDatePicker
                      id="dtLicenseeExpirtDate"
                      date
                      label="Expiry Date"
                      noOptionalLabel
                      stretch
                      defaultValue={
                        licence.expiryDate !== undefined
                          ? moment(licence.expiryDate).format('YYYY-MM-DD')
                          : undefined
                      }
                      onValueChanged={({ value }: { value: string }) => {
                        updateCompanyLicences({
                          licenceNumber: licence.licenceNumber,
                          expiryDate:
                            value === 'Invalid date' || value === ''
                              ? undefined
                              : moment(value).toDate(),
                          state: licence.state,
                          agreements: licence.agreements,
                          uuid: licence.uuid,
                        })
                      }}
                    />
                  </AgxRow>
                  <AgxRow veryLargeGap extraClasses="checkboxContainer">
                    {/*
               TODO: Enable Multi-select state dropdown when we have to introduce the functionality to the agents
               As per the new requirements in the card https://urbanx-development.atlassian.net/browse/AG-2272
               <AgxMultiSelect
                 id="companyState"
                 label="State"
                 hideOptionalLabel
                 defaultValue={companyStates}
                 onValueChanged={({ value }: { value: string }) => {
                   setCompanyStates(!value ? undefined : value?.split(','))
                 }}
                 options={stateOptions}
               /> */}
                    <AgxSelect
                      id="companyState"
                      label="State"
                      extraClasses="companyState"
                      options={agencyStateOptions.map((option) =>
                        companyLicences.some(
                          (l) => l.state?.toString() === option.value
                        )
                          ? { ...option, disabled: true }
                          : option
                      )}
                      defaultValue={{
                        label: licence.state
                          ? licence.state
                          : LicenceDetailsState.ACT,
                        value: licence.state
                          ? licence.state
                          : LicenceDetailsState.ACT,
                      }}
                      hideOptionalLabel
                      onValueChanged={({ value }: { value: string }) => {
                        updateCompanyLicences({
                          licenceNumber: licence.licenceNumber,
                          expiryDate: licence.expiryDate,
                          state: value as unknown as LicenceDetailsState,
                          agreements: licence.agreements,
                          uuid: licence.uuid,
                        })
                      }}
                    />
                    <AgxMultiSelect
                      id="agreements"
                      label="Agreements"
                      options={agreementOptions}
                      defaultValue={licence.agreements ?? []}
                      hideOptionalLabel
                      onValueChanged={({ value }: { value: string[] }) => {
                        if (value.length !== licence?.agreements?.length)
                          updateCompanyLicences({
                            licenceNumber: licence.licenceNumber,
                            expiryDate: licence.expiryDate,
                            state: licence.state,
                            agreements:
                              value?.map((v) => v as unknown as Agreements) ??
                              [],
                            uuid: licence.uuid,
                          })
                      }}
                    />
                  </AgxRow>
                </>
              )
            })}
            <AgxRow>
              <AgxButton
                text={'Add Licence'}
                primary
                medium
                disabled={companyLicences.length === 8}
                onClick={() => addLicence()}
              />
            </AgxRow>
          </AgxColumn>
          <AgxColumn veryLargeGap extraClasses="container50Percent">
            <AgxRow extraClasses="borderBottomContainer">
              <AgxLabel large>Contact details</AgxLabel>
            </AgxRow>
            <AgxRow veryLargeGap>
              <AgxTextInput
                id="listingsEmail"
                label="Listings Email"
                stretch
                noOptionalLabel
                defaultValue={listingsEmail || ''}
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    listingsEmail: value,
                  }))
                }
              />
            </AgxRow>
            <AgxRow>
              <AgxPhoneNumberInput
                id="phoneNumber"
                label="Phone Number"
                stretch
                noOptionalLabel
                parentControlValue={true}
                defaultValue={listingsPhone || ''}
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    listingsPhone: value,
                  }))
                }
              />
            </AgxRow>
          </AgxColumn>
          <AgxColumn veryLargeGap extraClasses="container50Percent">
            <AgxRow extraClasses="borderBottomContainer">
              <AgxLabel large>Address</AgxLabel>
            </AgxRow>
            <AgxRow>
              <AgxTextInput
                id="streetAddress"
                label="Street Address"
                stretch
                noOptionalLabel
                defaultValue={streetAddress || ''}
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    address: {
                      ...previousState.address,
                      streetAddress: value,
                    },
                  }))
                }
              />
            </AgxRow>
            <AgxRow veryLargeGap>
              <AgxTextInput
                id="suburb"
                label="Suburb"
                stretch
                noOptionalLabel
                defaultValue={suburb || ''}
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    address: {
                      ...previousState.address,
                      suburb: value,
                    },
                  }))
                }
              />

              <AgxSelect
                id="state"
                label="State"
                hideOptionalLabel
                options={allStateOptions}
                defaultValue={addressStateDefaultValue}
                onValueChanged={({ value }: { value: string }) => {
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    address: {
                      ...previousState.address,
                      state: value,
                    },
                  }))
                }}
              />

              <AgxTextInput
                id="postcode"
                label="Postcode"
                stretch
                noOptionalLabel
                defaultValue={postalCode || ''}
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    address: {
                      ...previousState.address,
                      postalCode: value,
                    },
                  }))
                }
              />
            </AgxRow>
          </AgxColumn>

          <AgxColumn veryLargeGap extraClasses="container50Percent">
            <AgxRow extraClasses="borderBottomContainer">
              <AgxLabel large>Extras</AgxLabel>
            </AgxRow>
            <AgxTextArea
              id="instructionsConditions"
              label="Reassignment Clause"
              defaultValue={instructionsConditions ?? ''}
              onInputValueChange={({ value }: { value: string }) =>
                setAgencyInformation((previousState) => ({
                  ...previousState,
                  instructionsConditions: value,
                }))
              }
              rows={6}
              maxLength={1100}
              showCharCount
              noOptionalLabel
              stretch
            />
          </AgxColumn>
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  )
}

export default AgencyDetailsPage
